import * as XLSX from 'xlsx';
import type { Source } from '../services/abrangencia';

/**
 * Representa uma linha planilhada da abrangência,
 * com a hierarquia completa: fonte → plataforma → busca → consulta → parâmetro.
 */
interface AbrangenciaRow {
  Plataforma: string;
  'Descrição Plataforma': string;
  Busca: string;
  'Descrição Busca': string;
  Consulta: string;
  'Descrição Consulta': string;
  'Label Parâmetro': string;
  Tag: string;
  Máscara: string;
  Obrigatório: string;
}

/**
 * Transforma a estrutura hierárquica de abrangência em linhas planas para a planilha.
 * Cada linha representa um parâmetro com toda a hierarquia acima dele preenchida.
 * Quando uma consulta não possui parâmetros, gera uma linha com os campos de parâmetro em branco.
 */
function flattenAbrangencia(sources: Source[]): AbrangenciaRow[] {
  const rows: AbrangenciaRow[] = [];

  sources.forEach((source) => {
    source.platforms.forEach((platform) => {
      platform.searches.forEach((search) => {
        search.queries.forEach((query) => {
          if (query.params.length === 0) {
            rows.push({
              Plataforma: platform.platform,
              'Descrição Plataforma': platform.description,
              Busca: search.search,
              'Descrição Busca': search.description,
              Consulta: query.query,
              'Descrição Consulta': query.description,
              'Label Parâmetro': '',
              Tag: '',
              Máscara: '',
              Obrigatório: '',
            });
          } else {
            query.params.forEach((param) => {
              rows.push({
                Plataforma: platform.platform,
                'Descrição Plataforma': platform.description,
                Busca: search.search,
                'Descrição Busca': search.description,
                Consulta: query.query,
                'Descrição Consulta': query.description,
                'Label Parâmetro': param.label,
                Tag: param.tag,
                Máscara: param.mask ?? '',
                Obrigatório: param.required ? 'Sim' : 'Não',
              });
            });
          }
        });
      });
    });
  });

  return rows;
}

/**
 * Aplica largura automática às colunas de uma planilha com base no conteúdo.
 */
function autoFitColumns(sheet: XLSX.WorkSheet, rows: AbrangenciaRow[]): void {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]) as (keyof AbrangenciaRow)[];
  const colWidths = headers.map((header) => {
    const maxDataLen = rows.reduce((max, row) => {
      const val = String(row[header] ?? '');
      return Math.max(max, val.length);
    }, 0);
    return { wch: Math.max(header.length, maxDataLen) + 2 };
  });

  sheet['!cols'] = colWidths;
}

/**
 * Gera e faz download de um arquivo XLSX com duas abas:
 * - "Pública": abrangência pública
 * - "Segredo de Justiça": abrangência de segredo de justiça
 */
export function exportAbrangenciaToXlsx(
  publicData: Source[],
  secretData: Source[],
): void {
  const workbook = XLSX.utils.book_new();

  const publicRows = flattenAbrangencia(publicData);
  const publicSheet = XLSX.utils.json_to_sheet(publicRows);
  autoFitColumns(publicSheet, publicRows);
  XLSX.utils.book_append_sheet(workbook, publicSheet, 'Pública');

  const secretRows = flattenAbrangencia(secretData);
  const secretSheet = XLSX.utils.json_to_sheet(secretRows);
  autoFitColumns(secretSheet, secretRows);
  XLSX.utils.book_append_sheet(workbook, secretSheet, 'Segredo de Justiça');

  XLSX.writeFile(workbook, 'abrangencia.xlsx');
}
